/** Client-side matching and sorting for the AI lists (personas, placements, digital assistants, provider models) over their snapshots. */
import { matchesDigitalAssistantDateFilters } from '~/lib/ai/digitalAssistant';
import type { SortOrder } from '~/lib/listParams';

const holds = (needle: string, values: (string | null | undefined)[]) =>
  values.some((value) => (value ?? '').toLowerCase().includes(needle));

/** True when `q` (any case, runs of spaces collapsed) is in the persona's name, id or description. */
export function matchesPersonaSearch(
  persona: { name: string; id: string; description?: string | null },
  q: string,
): boolean {
  const needle = q.trim().replace(/\s+/g, ' ').toLowerCase();
  return !needle || holds(needle, [persona.name, persona.id, persona.description]);
}

interface PlacementRow {
  name?: string | null;
  placementKey: string;
  componentId: string;
  description?: string | null;
  componentVersion?: string | null;
  model: string;
  status: string;
}

/** An empty `status` or `component` is no filter; `q` matches name, key, component, version, description or model. */
export function matchesPlacementFilters(
  placement: PlacementRow,
  { q, status, component }: { q: string; status: string; component: string },
): boolean {
  if (status && placement.status !== status) return false;
  if (component && placement.componentId !== component) return false;
  const needle = q.trim().toLowerCase();
  return (
    !needle ||
    holds(needle, [
      placement.name,
      placement.placementKey,
      placement.componentId,
      placement.componentVersion,
      placement.description,
      placement.model,
    ])
  );
}

export const ASSISTANT_SORT_FIELDS = ['name', 'key', 'status', 'updated', 'created'] as const;
export type AssistantSortField = (typeof ASSISTANT_SORT_FIELDS)[number];

interface AssistantRow {
  name: string;
  assistantKey: string;
  description?: string | null;
  status: string;
  draftModel?: string | null;
  agentId?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface AssistantFilters {
  /** Comma-joined statuses (`published,draft`); empty is every status. */
  status?: string;
  model?: string;
  agent?: string;
  createdFrom?: string;
  createdTo?: string;
  updatedFrom?: string;
  updatedTo?: string;
}

/** Every chosen filter must hold (an empty one is no filter); `q` matches name, key or description. */
export function matchesAssistantFilters(
  assistant: AssistantRow,
  { q, ...filters }: AssistantFilters & { q: string },
): boolean {
  if (filters.status && !filters.status.split(',').includes(assistant.status)) return false;
  if (filters.model && assistant.draftModel !== filters.model) return false;
  if (filters.agent && assistant.agentId !== filters.agent) return false;
  if (!matchesDigitalAssistantDateFilters(assistant, filters)) return false;
  const needle = q.trim().toLowerCase();
  return !needle || holds(needle, [assistant.name, assistant.assistantKey, assistant.description]);
}

const time = (iso: string) => new Date(iso).getTime();

/** Orders assistants by `field`; descending flips it. The input is not changed. */
export function sortAssistants<T extends AssistantRow>(
  rows: readonly T[],
  field: AssistantSortField,
  order: SortOrder,
): T[] {
  const compare = (left: T, right: T): number => {
    switch (field) {
      case 'name':
        return left.name.localeCompare(right.name);
      case 'key':
        return left.assistantKey.localeCompare(right.assistantKey);
      case 'status':
        return left.status.localeCompare(right.status);
      case 'updated':
        return time(left.updatedAt) - time(right.updatedAt);
      case 'created':
        return time(left.createdAt) - time(right.createdAt);
    }
  };
  return [...rows].sort((left, right) => compare(left, right) * (order === 'asc' ? 1 : -1));
}

interface ProviderModelRow {
  name: string;
  id: string;
  ownedBy: string;
}

/** True when `q` (any case) is in the model's name, id or owner. */
export function matchesProviderModelSearch(model: ProviderModelRow, q: string): boolean {
  const needle = q.trim().toLowerCase();
  return !needle || holds(needle, [model.name, model.id, model.ownedBy]);
}
